import type { WorkExperienceCompany, WorkExperienceRole } from '@applicator/shared'

import type { WorkExperienceGroupMode } from './workExperienceGroup'
import {
  cloneWorkExperienceCompanies,
  type WorkExperienceSortMode,
} from './workExperienceSort'

function normBulletText(text: string | undefined): string {
  return String(text ?? '').replace(/\s+/g, ' ').trim()
}

/** Per-role signature of bullet rows (count, ids, inclusion, text). Order within role is preserved. */
function roleBulletSignature(role: WorkExperienceRole): string {
  const bullets = role.bulletPoints || []
  return bullets
    .map((b) => `${b.id}|${b.included ? 1 : 0}|${normBulletText(b.text)}`)
    .join('\n')
}

function companiesRoleBulletSignatures(
  companies: WorkExperienceCompany[]
): Map<string, string> {
  const signatures = new Map<string, string>()
  for (const company of companies) {
    for (const role of company.roles || []) {
      if (role.id) {
        signatures.set(role.id, roleBulletSignature(role))
      }
    }
  }
  return signatures
}

/**
 * True when local manual-order backup no longer matches server work_experience content
 * (e.g. optimization or external PATCH added bullets while backup still has an old snapshot).
 */
export function isWorkExperienceSortBackupStale(
  serverCompanies: WorkExperienceCompany[],
  manualBackup: WorkExperienceCompany[] | null | undefined
): boolean {
  if (!manualBackup?.length) return false
  const serverSig = companiesRoleBulletSignatures(serverCompanies)
  const backupSig = companiesRoleBulletSignatures(manualBackup)
  if (serverSig.size !== backupSig.size) return true
  for (const [roleId, sig] of serverSig) {
    if (backupSig.get(roleId) !== sig) return true
  }
  return false
}

/**
 * Prefer manual sort backup for display order; fall back to fresh server rows when backup is stale.
 */
export function resolveWorkExperienceManualBackup(
  serverCompanies: WorkExperienceCompany[],
  stored: StoredWorkExperienceSortState | null
): WorkExperienceCompany[] {
  const backup = stored?.manualBackup
  if (!backup?.length) return serverCompanies
  if (isWorkExperienceSortBackupStale(serverCompanies, backup)) {
    return cloneWorkExperienceCompanies(serverCompanies)
  }
  return backup
}

export type StoredWorkExperienceSortState = {
  mode: WorkExperienceSortMode
  groupMode: WorkExperienceGroupMode
  manualBackup: WorkExperienceCompany[] | null
}

function storageKey(resumeId: string): string {
  return `applicator-resume-wx-sort-${resumeId}`
}

function parseGroupMode(value: unknown): WorkExperienceGroupMode {
  return value === 'position' ? 'position' : 'company'
}

export function loadWorkExperienceSortState(
  resumeId: string | undefined
): StoredWorkExperienceSortState | null {
  if (!resumeId || typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(storageKey(resumeId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredWorkExperienceSortState
    if (parsed.mode !== 'manual' && parsed.mode !== 'by_date') return null
    return {
      mode: parsed.mode,
      groupMode: parseGroupMode(parsed.groupMode),
      manualBackup: Array.isArray(parsed.manualBackup)
        ? parsed.manualBackup
        : null,
    }
  } catch {
    return null
  }
}

export function saveWorkExperienceSortState(
  resumeId: string | undefined,
  mode: WorkExperienceSortMode,
  manualBackup: WorkExperienceCompany[] | null,
  groupMode: WorkExperienceGroupMode = 'company'
): void {
  if (!resumeId || typeof window === 'undefined') return
  try {
    const payload: StoredWorkExperienceSortState = {
      mode,
      groupMode,
      manualBackup: manualBackup ? cloneWorkExperienceCompanies(manualBackup) : null,
    }
    window.localStorage.setItem(storageKey(resumeId), JSON.stringify(payload))
  } catch {
    // ignore quota / private mode
  }
}
